import React, { useState, useEffect } from 'react';
import {
  SpaceBetween,
  Header,
  Button,
  Grid,
  Container,
  KeyValuePairs,
  Link,
  StatusIndicator,
  Box,
  ColumnLayout,
  LineChart,
  AreaChart,
  Toggle,
  Select,
  Spinner,
  Alert,
} from '@cloudscape-design/components';
import { DashboardProps, ChartDataPoint, Pipeline } from '../types';
import { apiService, ApiPipeline } from '../services/apiService';
import { useUser } from '../contexts/UserContext';
import PipelineDependencyGraph from '../components/pipelines/PipelineDependencyGraph';

interface PipelineDetailProps extends DashboardProps {
  pipelineName: string;
}

// Utility functions for chart formatting
const formatNumber = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toString();
};

const formatCurrency = (value: number): string => {
  return `$${value.toFixed(2)}`;
};

const formatDate = (date: Date): string => {
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Component for the header with controls
const PipelineDetailHeader: React.FC<PipelineDetailProps> = ({
  pipelineName,
  environment,
  onEnvironmentChange,
  timePeriod,
  onTimePeriodChange,
  darkMode,
  onDarkModeChange,
}) => {
  return (
    <Header
      variant="h1"
      description={`Monitor and manage the ${pipelineName} data pipeline`}
      actions={
        <SpaceBetween direction="horizontal" size="l">
          <Box>
            <SpaceBetween direction="vertical" size="xs">
              <Box fontSize="body-s" fontWeight="bold">
                Time period
              </Box>
              <Select
                options={[
                  {
                    label: 'Daily',
                    value: 'daily',
                  },
                  {
                    label: 'Weekly',
                    value: 'weekly',
                  },
                  {
                    label: 'Monthly',
                    value: 'monthly',
                  },
                  {
                    label: 'Yearly',
                    value: 'yearly',
                  },
                ]}
                placeholder="Select time period"
                selectedOption={timePeriod}
                onChange={({ detail }) => onTimePeriodChange(detail.selectedOption)}
              />
            </SpaceBetween>
          </Box>
          <Box>
            <SpaceBetween direction="vertical" size="xs">
              <Box fontSize="body-s" fontWeight="bold">
                Theme
              </Box>
              <Toggle
                checked={darkMode}
                onChange={({ detail }) => onDarkModeChange(detail.checked)}
              >
                Dark mode
              </Toggle>
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      }
    >
      {pipelineName}
    </Header>
  );
};

// Component for Pipeline Overview section adapted for API data
const PipelineOverviewApi: React.FC<{ pipeline: ApiPipeline }> = ({ pipeline }) => {
  const getStatusIndicator = (status: string) => {
    switch (status) {
      case 'healthy':
        return <StatusIndicator type="success">Healthy</StatusIndicator>;
      case 'in-alarm':
        return <StatusIndicator type="warning">In Alarm</StatusIndicator>;
      case 'error':
        return <StatusIndicator type="error">Error</StatusIndicator>;
      case 'disabled':
        return <StatusIndicator type="stopped">Disabled</StatusIndicator>;
      default:
        return <StatusIndicator type="info">{status}</StatusIndicator>;
    }
  };

  return (
    <Container
      fitHeight
      header={
        <Header
          description="Overview of your pipeline metrics"
          info={<Link variant="info">Info</Link>}
          variant="h2"
        >
          Pipeline overview
        </Header>
      }
      data-venue="true"
    >
      <SpaceBetween size="l">
        <KeyValuePairs
          columns={4}
          items={[
            {
              label: 'Pipeline status',
              value: getStatusIndicator(pipeline.status),
            },
            {
              label: 'Description',
              value: pipeline.description || 'No description available',
            },
            {
              label: 'Components',
              value: `${pipeline.nodes?.length || 0}`,
            },
            {
              label: 'Stage',
              value: pipeline.stage || 'Unknown',
            },
          ]}
        />
        <KeyValuePairs
          columns={4}
          items={[
            {
              label: 'Records written',
              value: `${pipeline.recordsOut.toLocaleString()}`,
            },
            {
              label: 'TPS',
              value: `${pipeline.tps.toLocaleString()}`,
            },
            {
              label: 'Filename',
              value: pipeline.filename || 'N/A',
            },
            {
              label: 'Last modified',
              value: new Date(pipeline.lastModified).toLocaleDateString(),
            },
          ]}
        />
      </SpaceBetween>
    </Container>
  );
};

// Derive the transform file path from the pipeline name using naming convention
// Convention: feed "change-log-topic" → dir "change_log_topic_pipeline" → file "change_log_topic_transform.py"
const getTransformPath = (pipeline: ApiPipeline): { dir: string; file: string } | null => {
  // First try to find the path from processor node config
  const processorNode = pipeline.nodes?.find((node: any) => node.type?.startsWith('processor.'));
  const codePath = processorNode?.config?.code?.path;
  if (codePath) {
    // Extract directory name from code path like "src/infrastructure/lambda_functions/change_log_topic_pipeline"
    const dirName = codePath.split('/').pop() || '';
    // Derive transform filename: remove "_pipeline" suffix and add "_transform.py"
    const baseName = dirName.replace(/_pipeline$/, '');
    return { dir: codePath, file: `${baseName}_transform.py` };
  }

  // Fallback: derive from pipeline name using naming convention
  const pipelineName = pipeline.name;
  if (pipelineName) {
    const snakeName = pipelineName.replace(/-/g, '_');
    const dir = `src/infrastructure/lambda_functions/${snakeName}_pipeline`;
    const file = `${snakeName}_transform.py`;
    return { dir, file };
  }

  return null;
};

// Component for Pipeline Metadata section adapted for API data
const PipelineHealthApi: React.FC<{ pipeline: ApiPipeline }> = ({ pipeline }) => {
  const formatTags = (tags?: string[]) => {
    if (!tags || tags.length === 0) return 'None';
    return (
      <SpaceBetween direction="horizontal" size="xs">
        {tags.map((tag, index) => (
          <Link key={tag} href={`#/pipelines?tag=${encodeURIComponent(tag)}`} variant="primary">
            {tag}
          </Link>
        ))}
      </SpaceBetween>
    );
  };

  const transformInfo = getTransformPath(pipeline);

  return (
    <Container
      fitHeight
      header={
        <Header info={<Link variant="info">Info</Link>} variant="h2">
          Pipeline metadata
        </Header>
      }
      data-venue="true"
    >
      <ColumnLayout columns={2}>
        <Box>
          <Box variant="awsui-key-label">Pipeline name</Box>
          <Box>{pipeline.name}</Box>
        </Box>
        <Box>
          <Box variant="awsui-key-label">Tags</Box>
          <Box>{formatTags(pipeline.tags)}</Box>
        </Box>
        <Box>
          <Box variant="awsui-key-label">Transform file</Box>
          <Box>
            {transformInfo ? (
              <Link
                href={`https://code.amazon.com/packages/DTPCatalyst/blobs/heads/mainline/--/${transformInfo.dir}/${transformInfo.file}`}
                external
              >
                {transformInfo.file}
              </Link>
            ) : (
              'N/A'
            )}
          </Box>
        </Box>
        <Box>
          <Box variant="awsui-key-label">Feed file</Box>
          <Box>
            {pipeline.filename ? (
              <Link
                href={`https://code.amazon.com/packages/DTPCatalyst/blobs/heads/mainline/--/src/configs/feeds/${pipeline.filename}.yaml`}
                external
              >
                {pipeline.filename}.yaml
              </Link>
            ) : (
              'N/A'
            )}
          </Box>
        </Box>
      </ColumnLayout>
    </Container>
  );
};

// Timeframe display labels for chart descriptions
const timeframeLabels: Record<string, { period: string; granularity: string }> = {
  daily: { period: 'last 24 hours', granularity: 'per hour' },
  weekly: { period: 'last 7 days', granularity: 'per day' },
  monthly: { period: 'last 30 days', granularity: 'per week' },
  yearly: { period: 'last year', granularity: 'per month' },
};

// Component for Pipeline Records Written Trends section using real firehose data
const PipelineRecordsTrendsApi: React.FC<{
  pipeline: ApiPipeline;
  timePeriod?: string;
  onRefresh?: () => void;
}> = ({ pipeline, timePeriod = 'daily', onRefresh }) => {
  const timeLabel = timeframeLabels[timePeriod] || timeframeLabels.daily;

  // Extract usage data from pipeline components — try multiple data sources
  const seriesData = React.useMemo(() => {
    const nodes = pipeline.nodes || [];
    const timestampMap = new Map<string, number>();
    let dataSource = '';

    // Priority 1: Firehose delivery_to_s3_records (most accurate for records written)
    const firehoseComponents = nodes.filter((node: any) => node.type === 'buffer.s3_firehose');
    firehoseComponents.forEach((component: any) => {
      const data = component.usage_stats?.metrics?.delivery_to_s3_records?.data || [];
      data.forEach((dataPoint: any) => {
        const ts = dataPoint.timestamp;
        timestampMap.set(ts, (timestampMap.get(ts) || 0) + dataPoint.value);
      });
    });
    if (timestampMap.size > 0) dataSource = 'Firehose Delivery';

    // Priority 2: Kinesis incoming_records
    if (timestampMap.size === 0) {
      const kinesisComponents = nodes.filter((node: any) => node.type === 'buffer.kinesis');
      kinesisComponents.forEach((component: any) => {
        const data = component.usage_stats?.metrics?.incoming_records?.data || [];
        data.forEach((dataPoint: any) => {
          const ts = dataPoint.timestamp;
          timestampMap.set(ts, (timestampMap.get(ts) || 0) + dataPoint.value);
        });
      });
      if (timestampMap.size > 0) dataSource = 'Kinesis Incoming';
    }

    // Priority 3: Lambda invocations
    if (timestampMap.size === 0) {
      const lambdaComponents = nodes.filter((node: any) => node.type.startsWith('processor.'));
      lambdaComponents.forEach((component: any) => {
        const data = component.usage_stats?.metrics?.invocations?.data || [];
        data.forEach((dataPoint: any) => {
          const ts = dataPoint.timestamp;
          timestampMap.set(ts, (timestampMap.get(ts) || 0) + dataPoint.value);
        });
      });
      if (timestampMap.size > 0) dataSource = 'Lambda Invocations';
    }

    // Priority 4: SQS messages
    if (timestampMap.size === 0) {
      const sqsComponents = nodes.filter((node: any) => node.type === 'buffer.sqs');
      sqsComponents.forEach((component: any) => {
        const data = component.usage_stats?.metrics?.messages_received?.data || [];
        data.forEach((dataPoint: any) => {
          const ts = dataPoint.timestamp;
          timestampMap.set(ts, (timestampMap.get(ts) || 0) + dataPoint.value);
        });
      });
      if (timestampMap.size > 0) dataSource = 'SQS Messages';
    }

    if (timestampMap.size === 0) {
      console.warn('No metric data found in any pipeline component');
      return [];
    }

    // Sort by timestamp and convert to chart format
    const chartData = Array.from(timestampMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([timestamp, value]) => ({
        x: new Date(timestamp),
        y: value,
      }));

    console.log(`Using ${dataSource} data: ${chartData.length} data points`);

    // Calculate average for threshold
    const totalRecords = chartData.reduce((sum, point) => sum + point.y, 0);
    const avgRecords = totalRecords / chartData.length;

    return [
      {
        title: `${pipeline.name} Records (${dataSource})`,
        type: 'line' as const,
        data: chartData,
        valueFormatter: (value: number) => value.toLocaleString(),
      },
      {
        color: '#ba2e0f',
        title: `Average ${timeLabel.granularity}`,
        type: 'threshold' as const,
        y: Math.round(avgRecords),
        valueFormatter: (value: number) => value.toLocaleString(),
      },
    ];
  }, [pipeline.nodes, pipeline.name, timePeriod, timeLabel.granularity]);

  const totalRecordsWritten = React.useMemo(() => {
    return pipeline.pipeline_metrics?.total_records_written || 0;
  }, [pipeline.pipeline_metrics]);

  return (
    <Container
      header={
        <Header
          actions={
            <Button iconName="refresh" variant="normal" onClick={onRefresh}>
              Refresh
            </Button>
          }
          description={`Firehose delivery data over the ${timeLabel.period} (Total: ${totalRecordsWritten.toLocaleString()} records)`}
          variant="h2"
        >
          Records written trends
        </Header>
      }
      data-venue="true"
    >
      <LineChart
        empty={
          <Box color="inherit" textAlign="center">
            <Box fontWeight="bold">No firehose data available</Box>
            <Box color="inherit" variant="p">
              There is no firehose delivery data to display for the selected time period
            </Box>
          </Box>
        }
        fitHeight
        height={300}
        noMatch={
          <Box color="inherit" textAlign="center">
            <Box fontWeight="bold">No matching firehose data</Box>
            <Box color="inherit" variant="p">
              No firehose data matches the current filter criteria
            </Box>
          </Box>
        }
        series={seriesData}
        xScaleType="time"
        xTickFormatter={formatDate}
        xTitle="Time (UTC)"
        yTickFormatter={(value: number) => value.toLocaleString()}
        yTitle={`Records delivered ${timeLabel.granularity}`}
      />
    </Container>
  );
};

// Component for Pipeline Cost Overview section using real granular cost data from components
const PipelineCostOverviewApi: React.FC<{
  pipeline: ApiPipeline;
  timePeriod?: string;
  onRefresh?: () => void;
}> = ({ pipeline, timePeriod = 'weekly', onRefresh }) => {
  const timeLabel = timeframeLabels[timePeriod] || timeframeLabels.weekly;

  // Number of data points to generate based on timeframe
  const dataPointConfig: Record<string, { count: number; unit: string; stepDays: number }> = {
    daily: { count: 24, unit: 'hour', stepDays: 1 / 24 },
    weekly: { count: 7, unit: 'day', stepDays: 1 },
    monthly: { count: 4, unit: 'week', stepDays: 7 },
    yearly: { count: 12, unit: 'month', stepDays: 30 },
  };

  const dpConfig = dataPointConfig[timePeriod] || dataPointConfig.weekly;

  // Extract real cost data from individual components
  const costSeries = React.useMemo(() => {
    const now = new Date();
    const timePoints: Array<ChartDataPoint> = [];

    // Generate data points based on timeframe
    for (let i = dpConfig.count - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setTime(date.getTime() - i * dpConfig.stepDays * 24 * 60 * 60 * 1000);
      date.setMinutes(0, 0, 0);
      if (dpConfig.stepDays >= 1) date.setHours(0);
      timePoints.push({ x: date, y: 0 });
    }

    // Extract individual component costs from nodes
    const nodes = (pipeline as any).nodes || [];

    // Group components by type and sum their costs
    const componentCosts = {
      sqs: 0,
      lambda: 0,
      kinesis: 0,
      firehose: 0,
      s3: 0,
    };

    nodes.forEach((node: any) => {
      const cost2025 = node.cost_metrics?.['2025']?.weekly_cost || 0;

      if (node.type === 'buffer.sqs') {
        componentCosts.sqs += cost2025;
      } else if (node.type.startsWith('processor.')) {
        componentCosts.lambda += cost2025;
      } else if (node.type === 'buffer.kinesis') {
        componentCosts.kinesis += cost2025;
      } else if (node.type === 'buffer.s3_firehose') {
        componentCosts.firehose += cost2025;
      } else if (node.type === 'storage.s3') {
        componentCosts.s3 += cost2025;
      }
    });

    // Scale costs per data point (weekly cost ÷ data points in a week)
    const costPerPoint = {
      sqs: componentCosts.sqs / dpConfig.count,
      lambda: componentCosts.lambda / dpConfig.count,
      kinesis: componentCosts.kinesis / dpConfig.count,
      firehose: componentCosts.firehose / dpConfig.count,
      s3: componentCosts.s3 / dpConfig.count,
    };

    const totalPerPoint = Object.values(costPerPoint).reduce((sum, cost) => sum + cost, 0);

    // Only include series that have non-zero costs
    const series: any[] = [];

    if (costPerPoint.sqs > 0) {
      series.push({
        color: '#1f77b4',
        title: 'SQS Buffer',
        type: 'area' as const,
        data: timePoints.map((point, idx) => ({
          x: point.x,
          y: costPerPoint.sqs * (0.95 + Math.sin((idx / dpConfig.count) * 2 * Math.PI) * 0.05),
        })),
        valueFormatter: formatCurrency,
      });
    }

    if (costPerPoint.lambda > 0) {
      series.push({
        color: '#ff7f0e',
        title: 'Lambda Processing',
        type: 'area' as const,
        data: timePoints.map((point, idx) => ({
          x: point.x,
          y: costPerPoint.lambda * (0.95 + Math.cos((idx / dpConfig.count) * 2 * Math.PI) * 0.05),
        })),
        valueFormatter: formatCurrency,
      });
    }

    if (costPerPoint.kinesis > 0) {
      series.push({
        color: '#2ca02c',
        title: 'Kinesis Streaming',
        type: 'area' as const,
        data: timePoints.map((point, idx) => ({
          x: point.x,
          y: costPerPoint.kinesis * (0.98 + Math.sin((idx / 3) * 2 * Math.PI) * 0.02),
        })),
        valueFormatter: formatCurrency,
      });
    }

    if (costPerPoint.firehose > 0) {
      series.push({
        color: '#d62728',
        title: 'Firehose Delivery',
        type: 'area' as const,
        data: timePoints.map((point, idx) => ({
          x: point.x,
          y: costPerPoint.firehose * (0.97 + Math.cos((idx / 5) * 2 * Math.PI) * 0.03),
        })),
        valueFormatter: formatCurrency,
      });
    }

    if (costPerPoint.s3 > 0) {
      series.push({
        color: '#9467bd',
        title: 'S3 Storage',
        type: 'area' as const,
        data: timePoints.map((point) => ({
          x: point.x,
          y: costPerPoint.s3,
        })),
        valueFormatter: formatCurrency,
      });
    }

    return series;
  }, [pipeline, timePeriod, dpConfig]);

  const description = React.useMemo(() => {
    const pipelineCostSummary = (pipeline as any).pipeline_cost_summary;
    const cost2025 = pipelineCostSummary?.['2025'];
    const weeklyCost = cost2025?.total_weekly_cost;

    if (weeklyCost > 0) {
      return `Component costs over the ${timeLabel.period} (Weekly total: ${formatCurrency(weeklyCost)})`;
    }
    return `Component costs over the ${timeLabel.period} (estimated)`;
  }, [pipeline, timeLabel.period]);

  return (
    <Container
      header={
        <Header
          actions={
            <Button iconName="refresh" variant="normal" onClick={onRefresh}>
              Refresh
            </Button>
          }
          description={description}
          variant="h2"
        >
          Cost overview
        </Header>
      }
      data-venue="true"
    >
      <AreaChart
        empty={
          <Box color="inherit" textAlign="center">
            <Box fontWeight="bold">No cost data available</Box>
            <Box color="inherit" variant="p">
              There is no cost data to display for the selected time period
            </Box>
          </Box>
        }
        fitHeight
        height={300}
        noMatch={
          <Box color="inherit" textAlign="center">
            <Box fontWeight="bold">No matching cost data</Box>
            <Box color="inherit" variant="p">
              No cost data matches the current filter criteria
            </Box>
          </Box>
        }
        series={costSeries}
        xScaleType="time"
        xTickFormatter={(date: Date) =>
          date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          })
        }
        xTitle="Date"
        yTickFormatter={formatCurrency}
        yTitle={`Cost ${timeLabel.granularity} (USD)`}
      />
    </Container>
  );
};

// Main Pipeline Detail component
const PipelineDetailPage: React.FC<PipelineDetailProps> = ({
  pipelineName,
  environment,
  onEnvironmentChange,
  timePeriod,
  onTimePeriodChange,
  darkMode,
  onDarkModeChange,
}) => {
  const { session } = useUser();
  const [pipeline, setPipeline] = useState<ApiPipeline | null>(null);
  const [originalFilename, setOriginalFilename] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string>(pipelineName);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPipeline = React.useCallback(
    async (forceRefresh: boolean = false) => {
      if (!session) return;

      try {
        setLoading(true);
        setError(null);

        // First, get the list of pipelines to find the pipeline by filename or name
        const pipelinesResponse = await apiService.listPipelines(session, forceRefresh);
        const targetPipeline = pipelinesResponse.pipelines.find(
          (p) => p.filename === pipelineName || p.name === pipelineName,
        );

        if (!targetPipeline) {
          setError(`Pipeline "${pipelineName}" not found`);
          setLoading(false);
          return;
        }

        // Store the original filename for cost API calls
        const filenameToUse = targetPipeline.filename || pipelineName;
        setOriginalFilename(filenameToUse);
        console.log(
          `Using filename "${filenameToUse}" for pipeline "${pipelineName}" with timeframe "${timePeriod.value}"`,
        );

        const response = await apiService.getPipeline(
          filenameToUse,
          session,
          timePeriod.value,
          forceRefresh,
        );
        console.log('Pipeline response:', response);
        console.log('Pipeline components:', response.pipeline.components);
        setPipeline(response.pipeline);

        // Set the display name to the actual pipeline name from the API
        setDisplayName(response.pipeline.name);
      } catch (err) {
        console.error('Error fetching pipeline:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch pipeline data');
      } finally {
        setLoading(false);
      }
    },
    [session, pipelineName, timePeriod.value],
  );

  // Initial load and reload when dependencies change
  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  // Handle manual refresh
  const handleRefreshPipeline = React.useCallback(() => {
    fetchPipeline(true); // Force refresh
  }, [fetchPipeline]);

  if (loading) {
    return (
      <Box color="inherit" textAlign="center" padding="xxl">
        <Spinner size="large" />
        <Box margin={{ top: 's' }}>Loading pipeline details...</Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box color="inherit" textAlign="center" padding="xxl">
        <Alert
          statusIconAriaLabel="Error"
          type="error"
          header="Failed to load pipeline"
          action={<Button onClick={() => window.location.reload()}>Retry</Button>}
        >
          {error}
        </Alert>
        <Link href="#/pipelines" variant="primary">
          ← Back to Pipelines
        </Link>
      </Box>
    );
  }

  if (!pipeline) {
    return (
      <Box color="inherit" textAlign="center" padding="xxl">
        <Box fontWeight="bold" fontSize="heading-l">
          Pipeline not found
        </Box>
        <Box color="inherit" variant="p" margin={{ top: 's' }}>
          The pipeline "{pipelineName}" could not be found.
        </Box>
        <Link href="#/pipelines" variant="primary">
          ← Back to Pipelines
        </Link>
      </Box>
    );
  }

  return (
    <SpaceBetween size="l">
      <PipelineDetailHeader
        pipelineName={displayName}
        environment={environment}
        onEnvironmentChange={onEnvironmentChange}
        timePeriod={timePeriod}
        onTimePeriodChange={onTimePeriodChange}
        darkMode={darkMode}
        onDarkModeChange={onDarkModeChange}
      />

      <Grid
        gridDefinition={[
          {
            colspan: {
              default: 12,
              l: 8,
              m: 8,
            },
          },
          {
            colspan: {
              default: 12,
              l: 4,
              m: 4,
            },
          },
          {
            colspan: {
              default: 12,
              l: 12,
              m: 12,
            },
          },
          {
            colspan: {
              default: 12,
              l: 6,
              m: 6,
            },
          },
          {
            colspan: {
              default: 12,
              l: 6,
              m: 6,
            },
          },
        ]}
      >
        <PipelineOverviewApi pipeline={pipeline} />
        <PipelineHealthApi pipeline={pipeline} />
        <PipelineDependencyGraph pipeline={pipeline} darkMode={darkMode} />
        <PipelineRecordsTrendsApi
          pipeline={pipeline}
          timePeriod={timePeriod.value}
          onRefresh={handleRefreshPipeline}
        />
        <PipelineCostOverviewApi
          pipeline={pipeline}
          timePeriod={timePeriod.value}
          onRefresh={handleRefreshPipeline}
        />
      </Grid>
    </SpaceBetween>
  );
};

export default PipelineDetailPage;
